import React, { useState, useEffect } from "react";
import { UploadedFile } from "@/entities/UploadedFile";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Upload,
  FileText,
  Image as ImageIcon,
  File,
  Trash2,
  Copy,
  CheckCircle2,
  ArrowLeft,
  Search,
  SlidersHorizontal
} from "lucide-react";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function UploadedFiles() {
  const [files, setFiles] = useState([]);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("-created_date");
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadFiles();
  }, [sortBy]);

  const loadFiles = async () => {
    setIsLoading(true);
    const uploadedFiles = await UploadedFile.list(sortBy);
    setFiles(uploadedFiles);
    setIsLoading(false);
  };

  const handleDelete = async (fileId) => {
    await UploadedFile.delete(fileId);
    loadFiles();
  };

  const copyToClipboard = (url, fileId) => {
    try {
      // Obter a URL completa com o domínio atual
      const fullUrl = `${window.location.origin}${url}`;
      
      // Tentar copiar para a área de transferência
      navigator.clipboard.writeText(fullUrl)
        .then(() => {
          setCopiedUrl(fileId);
          setTimeout(() => setCopiedUrl(null), 2000);
        })
        .catch(err => {
          console.error("Erro ao copiar para a área de transferência:", err);
          // Fallback para quando a API Clipboard não está disponível
          const textArea = document.createElement("textarea");
          textArea.value = fullUrl;
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            setCopiedUrl(fileId);
            setTimeout(() => setCopiedUrl(null), 2000);
          } catch (e) {
            console.error("Fallback de cópia falhou:", e);
            alert("Não foi possível copiar a URL. Por favor, copie manualmente: " + fullUrl);
          }
          document.body.removeChild(textArea);
        });
    } catch (error) {
      console.error("Erro ao copiar URL:", error);
      alert("Não foi possível copiar a URL. Por favor, tente novamente.");
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith("image/")) return ImageIcon;
    if (fileType?.includes("pdf")) return FileText;
    return File;
  };

  const startEditing = (file) => {
    setEditingId(file.id);
    setEditingName(file.original_name);
  };

  const handleNameUpdate = async (fileId) => {
    await UploadedFile.update(fileId, { original_name: editingName });
    setEditingId(null);
    loadFiles();
  };

  const filteredFiles = files.filter(file => 
    file.original_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <Link to={createPageUrl("Dashboard")}>
          <Button variant="outline" size="icon">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Arquivos Enviados</h1>
          <p className="text-gray-500">
            Gerencie e visualize todos os seus arquivos
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div className="relative w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Pesquisar arquivos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full md:w-80"
          />
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-indigo-50" : ""}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-52">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-created_date">Mais recentes</SelectItem>
              <SelectItem value="created_date">Mais antigos</SelectItem>
              <SelectItem value="original_name">Nome (A-Z)</SelectItem>
              <SelectItem value="-original_name">Nome (Z-A)</SelectItem>
              <SelectItem value="-file_size">Tamanho (maior)</SelectItem>
              <SelectItem value="file_size">Tamanho (menor)</SelectItem>
            </SelectContent>
          </Select>
          
          <Link to={createPageUrl("Upload")}>
            <Button className="bg-sky-600 hover:bg-sky-700 w-full md:w-auto">
              <Upload className="w-4 h-4 mr-2" />
              Novo Upload
            </Button>
          </Link>
        </div>
      </div>

      {showFilters && (
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium mb-2">Tipo de Arquivo</p>
              <Select defaultValue="all">
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="image">Imagens</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="other">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Período</p>
              <Select defaultValue="all">
                <SelectTrigger>
                  <SelectValue placeholder="Qualquer período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer período</SelectItem>
                  <SelectItem value="today">Hoje</SelectItem>
                  <SelectItem value="week">Esta semana</SelectItem>
                  <SelectItem value="month">Este mês</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button className="w-full">Aplicar Filtros</Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          // Loading skeletons
          Array(6).fill(0).map((_, index) => (
            <Card key={index} className="p-4 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
                <div className="w-6 h-6 bg-gray-200 rounded-full" />
              </div>
              <div className="mt-4 h-8 bg-gray-200 rounded" />
            </Card>
          ))
        ) : filteredFiles.length > 0 ? (
          filteredFiles.map((file) => {
            const FileIcon = getFileIcon(file.file_type);
            return (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card className="p-4 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                        <FileIcon className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        {editingId === file.id ? (
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={() => handleNameUpdate(file.id)}
                            onKeyDown={(e) => e.key === 'Enter' && handleNameUpdate(file.id)}
                            className="h-7 text-sm"
                            autoFocus
                          />
                        ) : (
                          <h3 
                            className="font-medium text-gray-900 cursor-pointer hover:text-gray-600"
                            onClick={() => startEditing(file)}
                          >
                            {file.original_name}
                          </h3>
                        )}
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.file_size)}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-more-vertical"
                          >
                            <circle cx="8" cy="3" r="1" />
                            <circle cx="8" cy="8" r="1" />
                            <circle cx="8" cy="13" r="1" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Opções</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => startEditing(file)}>
                          Renomear
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyToClipboard(file.file_url, file.id)}>
                          Copiar URL
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(file.file_url, '_blank')}>
                          Visualizar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(file.id)}
                          className="text-red-600"
                        >
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <div className="flex-1 truncate bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-600">
                      {file.file_url}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(file.file_url, file.id)}
                      className="flex-shrink-0"
                    >
                      {copiedUrl === file.id ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })
        ) : (
          <div className="col-span-full">
            <Card className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum arquivo encontrado
              </h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 
                  `Não encontramos arquivos correspondentes a "${searchTerm}"` : 
                  "Comece fazendo upload do seu primeiro arquivo"}
              </p>
              <Link to={createPageUrl("Upload")}>
                <Button>Fazer Upload</Button>
              </Link>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}